// Pure leaderboard presentation helpers — no React, no DOM. Shared by the
// leaderboard modal / rank card and unit-tested directly.
//
// ORDER IS THE SERVER'S. public.leaderboard_ranked() (migration 164) is the one
// definition of how builders are ranked; nothing here re-ranks anyone. These
// helpers only split the payload into its two visible sections, keep the rows in
// the order the ranks already state, and phrase what the ranking did.

import type { LeaderboardEntry } from "@/lib/streak/types";

/**
 * The tie-break chain, in words, for the UI footnote. Mirrors keys 1–4 of the
 * SQL ordering; the last two keys (builder-since, then user id) exist only to
 * make the order total and are not worth explaining to a user.
 */
export const TIE_BREAK_EXPLAINER =
  "Ranked by current streak. Same streak? Best streak wins, then days built, then whoever built most recently.";

export interface LeaderboardSections {
  /** Builders holding a live streak, in rank order (1, 2, 3, … no ties). */
  ranked: LeaderboardEntry[];
  /**
   * Rows with no live streak — only ever the caller's own. Rendered under a
   * divider, without a rank, so a lapsed builder still sees where they stand
   * without being ranked among people who are actively building.
   */
  resting: LeaderboardEntry[];
}

/**
 * Split the payload into its ranked and resting sections and put the ranked rows
 * in rank order. Sorting here is belt-and-braces — the RPC already returns rank
 * order — but it means a row can never render above one with a lower rank, which
 * is the exact confusion this board is fixing. Ties on `rank` are impossible in
 * the new model; if one ever appeared, name order keeps it deterministic.
 */
export function splitLeaderboard(
  entries: readonly LeaderboardEntry[],
): LeaderboardSections {
  const ranked = entries.filter((e) => e.ranked && e.rank > 0);
  const resting = entries.filter((e) => !e.ranked || e.rank <= 0);
  ranked.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
  return { ranked, resting };
}

/**
 * "today" / "yesterday" / "3d ago" for a builder's last build, or null when the
 * date is missing or unparseable. Shown on each row so the recency tie-breaker
 * is visible rather than mysterious. Compared in UTC — the same calendar the
 * streak itself is counted in (migration 156).
 *
 * @param lastActiveDate ISO date, e.g. "2026-08-06".
 * @param today ISO date to compare against; defaults to the current UTC day.
 */
export function relativeBuildDay(
  lastActiveDate: string | null | undefined,
  today: string = utcToday(),
): string | null {
  const then = utcDayNumber(lastActiveDate);
  const now = utcDayNumber(today);
  if (then === null || now === null) return null;
  const days = now - then;
  if (days < 0) return "today"; // clock skew — never claim a future build
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

/** Today's date in UTC as an ISO day string. */
export function utcToday(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Days since the epoch for an ISO date, or null when it isn't a valid date. */
function utcDayNumber(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(ms)) return null;
  return Math.floor(ms / 86_400_000);
}

/**
 * The header line for a builder who isn't on the board. Distinguishes "your
 * streak lapsed" from "you've never built", because the fix differs only in
 * tone but the two read very differently to the user.
 */
export function unrankedHeadline(params: {
  totalRanked: number;
  totalActiveDays: number;
}): string {
  const { totalRanked, totalActiveDays } = params;
  const pool =
    totalRanked > 0
      ? `${totalRanked.toLocaleString()} builder${totalRanked === 1 ? "" : "s"} on a streak`
      : "the board";
  return totalActiveDays > 0
    ? `Your streak lapsed, so you're off the board. Build today to rejoin ${pool}.`
    : `You're not on the board yet. Build today to join ${pool}.`;
}
